import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Clock, Target, Briefcase, BookOpen, Quote } from "lucide-react";
import { useEffect } from "react";

/**
 * Reusable details modal for AI Solutions, Case Studies, Process steps, and Insights.
 *
 * Expected `item` shape (all fields optional except `name`):
 * {
 *   name, tagline, overview,
 *   highlights: string[]           // primary bullet list (e.g. capabilities, deliverables)
 *   highlightsLabel: string        // section label for highlights
 *   phases: [{ day, body }]        // milestones / phases timeline
 *   phasesLabel: string
 *   outcomes: string[]             // KPI list
 *   outcomesLabel: string
 *   examples: string[]             // recent client engagements with working examples
 *   examplesLabel: string
 *   quote: { body, attribution }   // optional pull-quote (insights / case studies)
 *   ctaPrimary: string             // override CTA label
 *   ctaSecondary: string
 * }
 */
export default function DetailsModal({ item, onClose, testIdPrefix = "details" }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    // Map our testIdPrefix to the kind that ChatWidget understands.
    const kindMap = {
      "ai-solution-details": "ai-solution",
      "case-study-details": "case-study",
      "process-details": "process",
      "insight-details": "insight",
    };
    const kind = kindMap[testIdPrefix] || "service";
    // Contextual chat invite after 25s of dwell time.
    const inviteTimer = setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("quasar:contextual-open", {
          detail: { kind, name: item?.name },
        })
      );
    }, 25000);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      clearTimeout(inviteTimer);
    };
  }, [onClose, item, testIdPrefix]);

  if (!item) return null;

  const ctaPrimary = item.ctaPrimary || "Book Strategy Call →";
  const ctaSecondary = item.ctaSecondary || "Run a Free Audit";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
        onClick={onClose}
        data-testid={`${testIdPrefix}-modal`}
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[#0B0B0E] border border-white/10 glow-purple"
        >
          <div className="sticky top-0 z-20 flex justify-end px-4 pt-4 pb-2 bg-gradient-to-b from-[#0B0B0E] via-[#0B0B0E]/95 to-transparent pointer-events-none">
            <button
              onClick={onClose}
              className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full glass-strong hover:bg-white/10 transition-colors shadow-lg"
              data-testid={`${testIdPrefix}-modal-close`}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-6 sm:px-10 pb-6 sm:pb-10 -mt-3">
            {item.eyebrow && (
              <div className="text-[10px] font-mono-acc uppercase tracking-[0.28em] text-cyan-300 mb-2">
                / {item.eyebrow}
              </div>
            )}
            <h3 className="font-clash text-2xl sm:text-4xl font-semibold tracking-tight leading-tight">
              {item.name}
            </h3>
            {item.tagline && (
              <p className="mt-2 text-cyan-300/90 text-sm font-mono-acc">{item.tagline}</p>
            )}

            {item.overview && (
              <p className="mt-6 text-zinc-200 leading-relaxed text-[15px]">
                {item.overview}
              </p>
            )}

            {item.highlights && item.highlights.length > 0 && (
              <Section icon={Briefcase} label={item.highlightsLabel || "What's inside"}>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                  {item.highlights.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                      <CheckCircle2 size={13} className="text-cyan-300 mt-0.5 shrink-0" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {item.phases && item.phases.length > 0 && (
              <Section icon={Clock} label={item.phasesLabel || "How it works"}>
                <div className="grid sm:grid-cols-3 gap-3">
                  {item.phases.map((m, i) => (
                    <div key={i} className="rounded-xl glass p-4">
                      <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-2">
                        {m.day}
                      </div>
                      <div className="text-sm text-zinc-200 leading-snug">{m.body}</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {item.outcomes && item.outcomes.length > 0 && (
              <Section icon={Target} label={item.outcomesLabel || "Typical outcomes"}>
                <div className="grid sm:grid-cols-2 gap-3">
                  {item.outcomes.map((k, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2 rounded-xl bg-gradient-to-br from-cyan-500/5 via-purple-500/5 to-fuchsia-500/5 border border-white/[0.06] p-3"
                    >
                      <span className="font-mono-acc text-cyan-300 text-xs mt-0.5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm text-zinc-200 leading-snug">{k}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {item.quote && (
              <div className="mt-8 rounded-2xl bg-gradient-to-br from-purple-500/10 via-cyan-500/5 to-transparent border border-purple-500/20 p-5">
                <Quote size={16} className="text-cyan-300 mb-3" />
                <p className="text-zinc-100 text-[15px] leading-relaxed italic">
                  "{item.quote.body}"
                </p>
                <p className="mt-3 text-xs text-zinc-400 font-mono-acc tracking-wider">
                  — {item.quote.attribution}
                </p>
              </div>
            )}

            {item.examples && item.examples.length > 0 && (
              <Section icon={CheckCircle2} label={item.examplesLabel || "Recent client engagements"}>
                <ul className="space-y-3">
                  {item.examples.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-300 leading-relaxed">
                      <span className="text-cyan-300 mt-1">›</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {item.takeaways && item.takeaways.length > 0 && (
              <Section icon={BookOpen} label={item.takeawaysLabel || "Key takeaways"}>
                <ul className="space-y-2">
                  {item.takeaways.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-200 leading-snug">
                      <span className="text-cyan-300 font-mono-acc text-xs mt-0.5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="#contact"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform"
                data-testid={`${testIdPrefix}-modal-cta-primary`}
              >
                {ctaPrimary}
              </a>
              <a
                href="#audit"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-full glass-strong px-6 py-3 text-sm hover:bg-white/10 transition-colors"
                data-testid={`${testIdPrefix}-modal-cta-secondary`}
              >
                {ctaSecondary}
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Section({ icon: Icon, label, children }) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 text-[11px] font-mono-acc uppercase tracking-[0.22em] text-zinc-400 mb-4">
        <Icon size={12} className="text-cyan-300" /> {label}
      </div>
      {children}
    </div>
  );
}
